import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Mic, Beer, Flame, Radio, ChevronUp, Lightbulb } from "lucide-react";
import VideoEmbed from "@/components/VideoEmbed";
import KineticMarquee from "@/components/KineticMarquee";
import { LATEST_VIDEO_ID, IMAGES, SOCIAL_LINKS, TAGLINE } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const [topIdeas, setTopIdeas] = useState([]);
  const [ideasLoading, setIdeasLoading] = useState(true);
  const [latest, setLatest] = useState({ id: LATEST_VIDEO_ID, title: null, episode: null });

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/suggestions`)
      .then((r) => mounted && setTopIdeas((r.data || []).slice(0, 3)))
      .catch(() => {})
      .finally(() => mounted && setIdeasLoading(false));

    axios
      .get(`${API}/videos`)
      .then((r) => {
        if (!mounted) return;
        const v = r.data?.videos?.[0];
        if (v?.id) {
          setLatest({ id: v.id, title: v.title || null, episode: v.episode ?? null });
        }
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b-2 border-[var(--tsws-green)]">
        <div className="relative px-6 lg:px-12 pt-8 pb-12 lg:pt-14 lg:pb-20 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Voicemail hero CTA — replaces the mascot, makes voicemail the hero feature */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <Link
              to="/voicemail"
              data-testid="hero-voicemail-card"
              className="block max-w-md mx-auto lg:mx-0 group"
            >
              <div className="relative border-2 border-[var(--tsws-green)] bg-[var(--tsws-paper)] p-8 lg:p-10 shadow-brutal hover-brutal">
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-label text-xs text-[var(--tsws-cigar)]">
                    ⏤ New / On-Air Feature
                  </span>
                </div>

                {/* Big mic */}
                <div className="relative mx-auto w-40 h-40 lg:w-44 lg:h-44 mb-6">
                  <div className="absolute inset-0 rounded-full border-4 border-[var(--tsws-green)] bg-[var(--tsws-cigar)] flex items-center justify-center group-hover:rotate-[-6deg] transition-transform">
                    <Mic
                      size={72}
                      strokeWidth={2.5}
                      className="text-[var(--tsws-paper)]"
                    />
                  </div>
                  {/* Pulse rings */}
                  <span className="absolute inset-0 rounded-full border-2 border-[var(--tsws-green)]/40 animate-ping" />
                </div>

                <h3 className="font-display text-5xl lg:text-6xl leading-[0.9] text-[var(--tsws-green)]">
                  leave sav<br />
                  a voicemail<span className="text-[var(--tsws-cigar)]">.</span>
                </h3>

                <p className="font-tag text-xl mt-3 text-[var(--tsws-green)]">
                  60 seconds. One take.
                </p>
                <p className="mt-2 leading-relaxed text-[var(--tsws-green)]/85">
                  Hot takes, questions, confessions, mid-bender ramblings — if it slaps, Sav plays
                  it on the show.
                </p>

                <div className="mt-7 flex items-center justify-between gap-3">
                  <span className="font-display text-lg px-5 py-3 border-2 border-[var(--tsws-green)] bg-[var(--tsws-green)] text-[var(--tsws-orange)] inline-flex items-center gap-2 group-hover:bg-[var(--tsws-cigar)] group-hover:border-[var(--tsws-cigar)] transition-colors">
                    tap to record <ArrowRight size={16} strokeWidth={2.8} />
                  </span>
                  <Radio
                    size={28}
                    className="text-[var(--tsws-green)]/60 group-hover:text-[var(--tsws-cigar)] transition-colors"
                  />
                </div>

                {/* Corner stamp */}
                <span
                  aria-hidden="true"
                  className="absolute -top-3 -right-3 font-display text-[var(--tsws-orange)] bg-[var(--tsws-cigar)] border-2 border-[var(--tsws-green)] text-sm px-3 py-1 rotate-[8deg]"
                >
                  on-air
                </span>
              </div>
            </Link>
          </div>

          {/* Text */}
          <div className="lg:col-span-7 order-1 lg:order-2 text-[var(--tsws-green)]">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-2 w-2 bg-[var(--tsws-cigar)] animate-pulse" />
              <span className="font-label text-xs">
                {latest.episode ? `Episode ${latest.episode} — Out Now` : "New Episode — Out Now"}
              </span>
            </div>
            <h1 className="font-display text-[20vw] sm:text-[16vw] lg:text-[11vw] leading-[0.82]">
              the sesh<span className="text-[var(--tsws-cigar)]">.</span>
            </h1>
            <p className="font-tag text-3xl md:text-5xl lg:text-6xl mt-2 leading-none">
              with <span className="underline decoration-4 underline-offset-4">Sav</span>
            </p>
            <p className="font-tag text-xl md:text-2xl mt-6 max-w-xl">
              {TAGLINE}
            </p>
            <p className="mt-5 max-w-xl text-lg leading-relaxed opacity-85">
              Weekly chats with rappers, fighters, comedians and Aussie icons — loose, honest, and
              poured over a cold one. Listen responsibly.
            </p>

            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/apply"
                data-testid="hero-apply-btn"
                className="font-display text-lg px-7 py-4 border-2 border-[var(--tsws-green)] bg-[var(--tsws-green)] text-[var(--tsws-orange)] hover-brutal hover:bg-[var(--tsws-cigar)] hover:border-[var(--tsws-cigar)] inline-flex items-center gap-3"
              >
                apply as a guest <ArrowRight size={18} strokeWidth={2.5} />
              </Link>
              <a
                href="#latest"
                data-testid="hero-watch-btn"
                className="font-display text-lg px-7 py-4 border-2 border-[var(--tsws-green)] text-[var(--tsws-green)] hover-brutal hover:bg-[var(--tsws-green)] hover:text-[var(--tsws-orange)] inline-flex items-center gap-3"
              >
                watch latest
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* KINETIC MARQUEE */}
      <KineticMarquee />

      {/* LATEST VIDEO */}
      <section id="latest" className="px-6 lg:px-12 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-4">
            <span className="font-label text-xs text-[var(--tsws-cigar)]">
              ⏤ Latest Episode
            </span>
            <h2 className="font-display text-6xl lg:text-8xl mt-3 leading-none text-[var(--tsws-green)]">
              fresh pour<span className="text-[var(--tsws-cigar)]">.</span>
            </h2>
            <p className="font-tag text-xl mt-4">Hit play. Drop in mid-conversation.</p>
            <p className="mt-4 leading-relaxed opacity-85">
              This is the most recent episode straight from the tap — unfiltered, uncut, and
              exactly how Sav likes it.
            </p>
            <div className="mt-8 space-y-3 text-sm">
              <div className="flex justify-between gap-3 border-b-2 border-[var(--tsws-green)]/40 pb-2">
                <span className="font-label shrink-0">Episode</span>
                <span className="font-tag text-lg">{latest.episode ? `#${latest.episode}` : "Latest"}</span>
              </div>
              <div className="border-b-2 border-[var(--tsws-green)]/40 pb-2">
                <div className="font-label mb-1">Title</div>
                <div className="font-tag text-lg leading-snug">
                  {latest.title || "Fresh from the studio"}
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-8">
            <div className="shadow-brutal">
              <VideoEmbed
                id={latest.id}
                title={latest.title || "Latest TSWS episode"}
                testId="latest-video-player"
              />
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/videos"
                data-testid="see-all-videos"
                className="font-display text-base px-5 py-3 border-2 border-[var(--tsws-green)] text-[var(--tsws-green)] hover:bg-[var(--tsws-green)] hover:text-[var(--tsws-orange)]"
              >
                see all episodes →
              </Link>
              <Link
                to="/audio"
                data-testid="listen-instead"
                className="font-display text-base px-5 py-3 border-2 border-[var(--tsws-green)] text-[var(--tsws-green)] hover:bg-[var(--tsws-green)] hover:text-[var(--tsws-orange)]"
              >
                listen instead →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* IDEAS STRIP — Suggestions board promoted as hero feature */}
      <section
        data-testid="ideas-strip"
        className="border-y-2 border-[var(--tsws-green)] bg-[var(--tsws-orange-light)]"
      >
        <div className="px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-stretch">
          {/* Left: pitch card */}
          <div className="lg:col-span-5 flex">
            <Link
              to="/suggest"
              data-testid="home-ideas-card"
              className="block w-full group"
            >
              <div className="relative h-full border-2 border-[var(--tsws-green)] bg-[var(--tsws-paper)] p-8 lg:p-10 shadow-brutal hover-brutal">
                <span
                  aria-hidden="true"
                  className="absolute -top-3 -left-3 font-display text-[var(--tsws-paper)] bg-[var(--tsws-green)] border-2 border-[var(--tsws-green)] text-sm px-3 py-1 -rotate-[6deg]"
                >
                  community-voted
                </span>

                <div className="flex items-center gap-3 mb-5 mt-2">
                  <Lightbulb className="text-[var(--tsws-cigar)]" size={28} />
                  <span className="font-label text-xs text-[var(--tsws-cigar)]">
                    ⏤ Solo Pod Suggestions
                  </span>
                </div>

                <h3 className="font-display text-5xl lg:text-7xl leading-[0.9] text-[var(--tsws-green)]">
                  pitch sav<br />
                  an idea<span className="text-[var(--tsws-cigar)]">.</span>
                </h3>
                <p className="font-tag text-xl mt-3">For the solo episodes.</p>
                <p className="mt-2 leading-relaxed opacity-90">
                  Drop a topic, vote on the best ones. The loudest ideas land on the next solo
                  episode.
                </p>

                <div className="mt-7">
                  <span className="font-display text-lg px-5 py-3 border-2 border-[var(--tsws-green)] bg-[var(--tsws-green)] text-[var(--tsws-orange)] inline-flex items-center gap-2 group-hover:bg-[var(--tsws-cigar)] group-hover:border-[var(--tsws-cigar)] transition-colors">
                    drop &amp; vote <ArrowRight size={16} strokeWidth={2.8} />
                  </span>
                </div>
              </div>
            </Link>
          </div>

          {/* Right: top community ideas live preview */}
          <div className="lg:col-span-7">
            <div className="flex items-end justify-between mb-5">
              <h4 className="font-display text-4xl lg:text-5xl leading-none text-[var(--tsws-green)]">
                top picks<span className="text-[var(--tsws-cigar)]">.</span>
              </h4>
              <Link
                to="/suggest"
                data-testid="home-ideas-see-all"
                className="font-display text-base px-4 py-2 border-2 border-[var(--tsws-green)] text-[var(--tsws-green)] hover:bg-[var(--tsws-green)] hover:text-[var(--tsws-orange)] hidden sm:inline-block"
              >
                see all →
              </Link>
            </div>

            {ideasLoading && (
              <div className="font-label text-sm opacity-60">Loading ideas...</div>
            )}

            {!ideasLoading && topIdeas.length === 0 && (
              <Link
                to="/suggest"
                data-testid="home-ideas-empty"
                className="block border-2 border-dashed border-[var(--tsws-green)] bg-[var(--tsws-paper)] p-8 lg:p-10 hover:bg-[var(--tsws-cream)]"
              >
                <Lightbulb className="text-[var(--tsws-cigar)] mb-3" size={32} />
                <div className="font-display text-3xl">be the first.</div>
                <p className="font-tag text-lg mt-1 opacity-80">
                  No ideas yet — pitch one and own the top spot.
                </p>
              </Link>
            )}

            {!ideasLoading && topIdeas.length > 0 && (
              <ul className="space-y-3" data-testid="home-ideas-list">
                {topIdeas.map((it, idx) => (
                  <li
                    key={it.id}
                    data-testid={`home-idea-${it.id}`}
                    className="border-2 border-[var(--tsws-green)] bg-[var(--tsws-paper)] p-4 lg:p-5 flex gap-4 items-start"
                  >
                    <div className="shrink-0 w-14 flex flex-col items-center justify-center py-2 border-2 border-[var(--tsws-green)] bg-[var(--tsws-green)] text-[var(--tsws-orange)]">
                      <ChevronUp size={20} strokeWidth={2.8} />
                      <span className="font-display text-xl leading-none mt-0.5">
                        {it.upvotes || 0}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-display text-[10px] px-2 py-0.5 bg-[var(--tsws-green)] text-[var(--tsws-orange)] uppercase">
                          {it.category}
                        </span>
                        {idx === 0 && (
                          <span className="font-label text-[10px] text-[var(--tsws-cigar)]">
                            ⏤ #1
                          </span>
                        )}
                      </div>
                      <h5 className="font-display text-xl lg:text-2xl leading-tight">
                        {it.title.toLowerCase()}
                      </h5>
                      <p className="mt-1 font-tag text-sm opacity-70">
                        — {it.name?.trim() || "anonymous"}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <Link
              to="/suggest"
              data-testid="home-ideas-mobile-see-all"
              className="sm:hidden mt-4 font-display text-base px-4 py-2 border-2 border-[var(--tsws-green)] text-[var(--tsws-green)] hover:bg-[var(--tsws-green)] hover:text-[var(--tsws-orange)] inline-block"
            >
              see all ideas →
            </Link>
          </div>
        </div>
      </section>

      {/* WHO IS SAV */}
      <section className="border-y-2 border-[var(--tsws-green)] bg-[var(--tsws-cream)]">
        <div className="px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-stretch">
          <div className="lg:col-span-5 relative min-h-[420px]">
            <div className="absolute inset-0 border-2 border-[var(--tsws-green)] overflow-hidden">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${IMAGES.beers})`,
                  filter: "sepia(0.5) saturate(1.2) hue-rotate(-10deg) contrast(1.1)",
                }}
              />
              <div className="absolute inset-0 bg-[var(--tsws-orange)]/40 mix-blend-multiply" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="font-display text-6xl lg:text-8xl leading-none text-[var(--tsws-green)]">
                  cheers<span className="text-[var(--tsws-cigar)]">.</span>
                </div>
                <p className="font-tag text-xl mt-2 text-[var(--tsws-green)]">Listen responsibly.</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 lg:pl-8 text-[var(--tsws-green)]">
            <span className="font-label text-xs text-[var(--tsws-cigar)]">⏤ Who is Sav?</span>
            <h2 className="font-display text-5xl lg:text-7xl mt-3 leading-[0.9]">
              one bloke.
              <br />
              <span className="text-stroke">real convos.</span>
            </h2>
            <p className="mt-8 text-lg leading-relaxed max-w-2xl">
              Sav started The Sesh chasing the kind of conversations you'd hear at a pub after the
              third schooner — the loose, honest ones nobody puts on camera. 200+ episodes later,
              he's still doing exactly that, but the guest list now includes rappers, world-class
              fighters, comedians and the occasional politician.
            </p>
            <p className="mt-4 leading-relaxed max-w-2xl opacity-80">
              No corporate sponsors. No PR-approved questions. Just two people, one mic, and however
              long it takes to get to something real.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              <div className="border-2 border-[var(--tsws-green)] p-4 bg-[var(--tsws-paper)]">
                <Mic className="text-[var(--tsws-cigar)] mb-2" size={22} />
                <div className="font-display text-3xl">210+</div>
                <div className="font-label text-xs opacity-70">Episodes</div>
              </div>
              <div className="border-2 border-[var(--tsws-green)] p-4 bg-[var(--tsws-paper)]">
                <Beer className="text-[var(--tsws-cigar)] mb-2" size={22} />
                <div className="font-display text-3xl">4</div>
                <div className="font-label text-xs opacity-70">Years In</div>
              </div>
              <div className="border-2 border-[var(--tsws-green)] p-4 bg-[var(--tsws-paper)]">
                <Flame className="text-[var(--tsws-cigar)] mb-2" size={22} />
                <div className="font-display text-3xl">∞</div>
                <div className="font-label text-xs opacity-70">Banter</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA: APPLY */}
      <section className="px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8 text-[var(--tsws-green)]">
            <span className="font-label text-xs text-[var(--tsws-cigar)]">⏤ Be On The Sesh</span>
            <h2 className="font-display text-5xl md:text-7xl lg:text-8xl mt-3 leading-[0.9]">
              got
              <br />
              something
              <br />
              <span className="text-stroke">to say?</span>
            </h2>
          </div>
          <div className="lg:col-span-4 text-[var(--tsws-green)]">
            <p className="text-lg leading-relaxed mb-7 opacity-90">
              Musicians, fighters, comedians, hustlers, troublemakers — if you've got a story worth
              an hour, get in touch. No agents. No gatekeepers. Just the form.
            </p>
            <Link
              to="/apply"
              data-testid="home-apply-cta"
              className="inline-block font-display text-xl px-8 py-5 bg-[var(--tsws-cigar)] border-2 border-[var(--tsws-cigar)] text-[var(--tsws-paper)] hover-brutal hover:bg-[var(--tsws-green)] hover:border-[var(--tsws-green)]"
            >
              fill in the application →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
